/**
 * Browse-region video activation controller (PBI #1142).
 *
 * Catalog e-card tiles (`TemplateSelectionTile` and authenticated `TemplateCard`)
 * must not mount full MP4 `src` on cold load. This provider activates every
 * animated tile that is sufficiently visible (after a short poster-first delay)
 * so all on-screen cards can play together. Desktop hover / explicit play still
 * pin a tile immediately without deactivating other visible players.
 *
 * Wrap every user-facing e-card browse strip / grid: landing Explore, `/cards`,
 * Overview carousel, Schedule Card picker, confirmation upsell, and `/templates`.
 *
 * Import this module by path (not via `components/common` barrel) from landing
 * code so eager LandingPage init does not hit the common ↔ templates circular
 * dependency graph and leave the provider undefined at first paint.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * Minimum intersection ratio before a tile may visibility-autoplay.
 *
 * Kept low so partially on-screen carousel tiles (common when the Explore strip
 * first enters the viewport) still autoplay instead of waiting for a play tap.
 */
export const PUBLIC_BROWSE_VIDEO_MIN_VISIBILITY_RATIO = 0.1;

/**
 * Delay after a tile qualifies as visible before mounting MP4.
 *
 * Keeps poster-first cold paint (LCP / CWV) while still replacing the still with
 * a playing animation once the card is on screen.
 */
export const PUBLIC_BROWSE_VIDEO_VISIBILITY_ACTIVATE_DELAY_MS = 250;

/**
 * Faster poster→MP4 swap on coarse pointers (phones). Desktop keeps the longer
 * delay for LCP; phones have no hover so visibility autoplay is the only path
 * and a long delay feels like "I have to press play."
 */
export const PUBLIC_BROWSE_VIDEO_COARSE_POINTER_ACTIVATE_DELAY_MS = 100;

/**
 * Returns the poster-first delay for the current pointer type.
 */
export function getPublicBrowseVideoVisibilityActivateDelayMs(): number {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return PUBLIC_BROWSE_VIDEO_VISIBILITY_ACTIVATE_DELAY_MS;
  }

  const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  return coarse
    ? PUBLIC_BROWSE_VIDEO_COARSE_POINTER_ACTIVATE_DELAY_MS
    : PUBLIC_BROWSE_VIDEO_VISIBILITY_ACTIVATE_DELAY_MS;
}

/**
 * Fallback intersection ratio from layout boxes when IntersectionObserver is
 * slow to deliver its first callback (common right after a carousel paint).
 */
function getViewportIntersectionRatio(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
  const visibleHeight = Math.max(
    0,
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
  );
  const area = rect.width * rect.height;
  if (area <= 0 || visibleWidth <= 0 || visibleHeight <= 0) {
    return 0;
  }

  return (visibleWidth * visibleHeight) / area;
}

/**
 * Stable empty set so the initial context value does not allocate every render.
 */
const EMPTY_ACTIVE_IDS: ReadonlySet<number> = new Set();

interface PublicBrowseVideoContextValue {
  /**
   * Template ids currently allowed to mount an MP4 via visibility autoplay
   * and/or hover / play pin.
   */
  activeTemplateIds: ReadonlySet<number>;
  /** Pin this template via hover / play (additive; does not stop other visibles). */
  requestActivation: (templateId: number) => void;
  /** Clear hover / play pin only when this template is still the pinned one. */
  releaseActivation: (templateId: number) => void;
  /**
   * Report how much of this tile intersects the viewport (0 = gone).
   * Every id at or above {@link PUBLIC_BROWSE_VIDEO_MIN_VISIBILITY_RATIO}
   * becomes active after the poster-first delay.
   */
  reportVisibility: (templateId: number, intersectionRatio: number) => void;
}

const PublicBrowseVideoContext = createContext<PublicBrowseVideoContextValue | null>(null);

interface PublicBrowseVideoProviderProps {
  children: ReactNode;
}

/**
 * Owns the set of active public-browse video ids for a page region.
 *
 * All sufficiently visible animated tiles may play concurrently. Hover / play
 * pins one id immediately without clearing the visibility-active set.
 *
 * Regression coverage: `PublicBrowseVideoContext.test.tsx` (“every visible
 * tile”) and `e2e/public.spec.ts` (two on-screen Explore MP4s). Do not revert
 * to a single “most-visible winner” — that froze later tiles on Dev.
 */
export const PublicBrowseVideoProvider = ({ children }: PublicBrowseVideoProviderProps) => {
  const [hoverPinnedId, setHoverPinnedId] = useState<number | null>(null);
  const [visibilityActiveIds, setVisibilityActiveIds] =
    useState<ReadonlySet<number>>(EMPTY_ACTIVE_IDS);
  const visibilityRatiosRef = useRef(new Map<number, number>());
  const visibilityActiveIdsRef = useRef<ReadonlySet<number>>(EMPTY_ACTIVE_IDS);
  const pendingVisibilityTimersRef = useRef(new Map<number, number>());

  const clearVisibilityDelayTimer = useCallback((templateId: number) => {
    const timerId = pendingVisibilityTimersRef.current.get(templateId);
    if (timerId == null) {
      return;
    }

    window.clearTimeout(timerId);
    pendingVisibilityTimersRef.current.delete(templateId);
  }, []);

  const clearAllVisibilityDelayTimers = useCallback(() => {
    for (const timerId of pendingVisibilityTimersRef.current.values()) {
      window.clearTimeout(timerId);
    }
    pendingVisibilityTimersRef.current.clear();
  }, []);

  /**
   * Syncs delayed activation with the current visibility map so every
   * qualifying tile plays (not just the single most-visible winner).
   */
  const syncVisibilityActives = useCallback(() => {
    const qualifyingIds = new Set<number>();
    for (const [templateId, ratio] of visibilityRatiosRef.current) {
      if (ratio >= PUBLIC_BROWSE_VIDEO_MIN_VISIBILITY_RATIO) {
        qualifyingIds.add(templateId);
      }
    }

    // Drop tiles that left the viewport immediately (cancel pending + active).
    for (const templateId of [...pendingVisibilityTimersRef.current.keys()]) {
      if (!qualifyingIds.has(templateId)) {
        clearVisibilityDelayTimer(templateId);
      }
    }

    const previousActive = visibilityActiveIdsRef.current;
    const nextActive = new Set<number>();
    for (const templateId of previousActive) {
      if (qualifyingIds.has(templateId)) {
        nextActive.add(templateId);
      }
    }

    const activeChanged =
      nextActive.size !== previousActive.size ||
      [...nextActive].some((id) => !previousActive.has(id));

    if (activeChanged) {
      const frozen = nextActive.size === 0 ? EMPTY_ACTIVE_IDS : nextActive;
      visibilityActiveIdsRef.current = frozen;
      setVisibilityActiveIds(frozen);
    }

    // Schedule poster-first activation for newly qualifying tiles.
    for (const templateId of qualifyingIds) {
      if (visibilityActiveIdsRef.current.has(templateId)) {
        continue;
      }

      if (pendingVisibilityTimersRef.current.has(templateId)) {
        continue;
      }

      const timerId = window.setTimeout(() => {
        pendingVisibilityTimersRef.current.delete(templateId);
        // Re-check ratio in case the tile scrolled away during the delay.
        const ratio = visibilityRatiosRef.current.get(templateId) ?? 0;
        if (ratio < PUBLIC_BROWSE_VIDEO_MIN_VISIBILITY_RATIO) {
          return;
        }

        if (visibilityActiveIdsRef.current.has(templateId)) {
          return;
        }

        const withId = new Set(visibilityActiveIdsRef.current);
        withId.add(templateId);
        visibilityActiveIdsRef.current = withId;
        setVisibilityActiveIds(withId);
      }, getPublicBrowseVideoVisibilityActivateDelayMs());

      pendingVisibilityTimersRef.current.set(templateId, timerId);
    }
  }, [clearVisibilityDelayTimer]);

  const reportVisibility = useCallback(
    (templateId: number, intersectionRatio: number) => {
      if (intersectionRatio <= 0) {
        visibilityRatiosRef.current.delete(templateId);
      } else {
        visibilityRatiosRef.current.set(templateId, intersectionRatio);
      }

      syncVisibilityActives();
    },
    [syncVisibilityActives],
  );

  const requestActivation = useCallback((templateId: number) => {
    setHoverPinnedId(templateId);
  }, []);

  const releaseActivation = useCallback((templateId: number) => {
    setHoverPinnedId((current) => (current === templateId ? null : current));
  }, []);

  useEffect(
    () => () => {
      clearAllVisibilityDelayTimers();
    },
    [clearAllVisibilityDelayTimers],
  );

  const activeTemplateIds = useMemo(() => {
    if (hoverPinnedId == null) {
      return visibilityActiveIds;
    }

    if (visibilityActiveIds.has(hoverPinnedId)) {
      return visibilityActiveIds;
    }

    const next = new Set(visibilityActiveIds);
    next.add(hoverPinnedId);
    return next;
  }, [hoverPinnedId, visibilityActiveIds]);

  const value = useMemo(
    () => ({
      activeTemplateIds,
      requestActivation,
      releaseActivation,
      reportVisibility,
    }),
    [activeTemplateIds, requestActivation, releaseActivation, reportVisibility],
  );

  return (
    <PublicBrowseVideoContext.Provider value={value}>{children}</PublicBrowseVideoContext.Provider>
  );
};

interface UsePublicBrowseVideoActivationResult {
  /**
   * When true, the tile may mount `<video src={mp4}>` and autoplay.
   * Public visual tiles without a provider stay poster-only (fail closed for CWV).
   */
  isVideoActive: boolean;
  /** Activate this template via hover / play (additive pin). No-op when disabled. */
  activateVideo: () => void;
  /** Release hover / play pin when this template still owns it. */
  deactivateVideo: () => void;
  /** True when a {@link PublicBrowseVideoProvider} wraps the tree. */
  hasController: boolean;
}

/**
 * Returns video activation state for one public e-card tile.
 *
 * When {@link elementRef} is provided, reports viewport intersection so the
 * provider can autoplay every sufficiently visible animated tile after the
 * poster-first delay — without requiring a play tap.
 *
 * @param templateId Card template id used as the activation key.
 * @param enabled When false (non-video / default variant), never activates.
 * @param elementRef Optional tile root to observe for visibility-driven autoplay.
 */
export const usePublicBrowseVideoActivation = (
  templateId: number,
  enabled: boolean,
  elementRef?: RefObject<HTMLElement | null>,
): UsePublicBrowseVideoActivationResult => {
  const ctx = useContext(PublicBrowseVideoContext);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const activateVideo = useCallback(() => {
    if (!enabled || !ctx) {
      return;
    }

    ctx.requestActivation(templateId);
  }, [ctx, enabled, templateId]);

  const deactivateVideo = useCallback(() => {
    if (!enabled || !ctx) {
      return;
    }

    ctx.releaseActivation(templateId);
  }, [ctx, enabled, templateId]);

  // Observe visibility without depending on ctx identity — otherwise every
  // activation update would disconnect, report ratio 0, and clear actives.
  // useLayoutEffect + rAF retry so the observer attaches as soon as the tile
  // root exists; a geometry fallback covers the first-paint IO gap so users do
  // not have to tap play while waiting for the browser callback.
  useLayoutEffect(() => {
    if (!enabled || !elementRef) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    let rafId = 0;
    let removeViewportListeners: (() => void) | undefined;

    const reportRatio = (rawRatio: number, isIntersecting: boolean) => {
      const ratio = isIntersecting
        ? Math.max(rawRatio, PUBLIC_BROWSE_VIDEO_MIN_VISIBILITY_RATIO)
        : 0;
      ctxRef.current?.reportVisibility(templateId, ratio);
    };

    const reportGeometry = (element: HTMLElement) => {
      const ratio = getViewportIntersectionRatio(element);
      reportRatio(ratio, ratio > 0);
    };

    const attach = () => {
      if (cancelled) {
        return;
      }

      const controller = ctxRef.current;
      const element = elementRef.current;
      if (!controller || !element) {
        rafId = window.requestAnimationFrame(attach);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) {
            return;
          }

          // Any intersecting tile is a candidate. Browsers sometimes report
          // ratio 0 with isIntersecting=true at the threshold:0 boundary —
          // floor that to the minimum so partially visible carousel tiles
          // still autoplay instead of waiting for a play tap.
          reportRatio(entry.intersectionRatio, entry.isIntersecting);
        },
        {
          root: null,
          // Expand the hit box so tiles entering the Explore strip (vertical
          // scroll) or peeking into a horizontal carousel still qualify.
          rootMargin: '120px 40px 120px 40px',
          threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1],
        },
      );

      observer.observe(element);

      // Immediate geometry pass — IO's first callback can arrive after paint,
      // which left visible tiles on the play control until the user tapped.
      reportGeometry(element);

      const onViewportChange = () => {
        if (cancelled || !elementRef.current) {
          return;
        }

        reportGeometry(elementRef.current);
      };

      window.addEventListener('scroll', onViewportChange, { passive: true, capture: true });
      window.addEventListener('resize', onViewportChange);
      removeViewportListeners = () => {
        window.removeEventListener('scroll', onViewportChange, true);
        window.removeEventListener('resize', onViewportChange);
      };
    };

    attach();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      removeViewportListeners?.();
      observer?.disconnect();
      ctxRef.current?.reportVisibility(templateId, 0);
    };
  }, [elementRef, enabled, templateId]);

  return {
    isVideoActive: Boolean(enabled && ctx?.activeTemplateIds.has(templateId)),
    activateVideo,
    deactivateVideo,
    hasController: ctx !== null,
  };
};
